import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Download,
  FileText,
} from "lucide-react";
import {
  API,
  fmtDate,
  fmtBytes,
} from "./shared";

export function ClientRecordsTab({ authHeader }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/records`, { headers: authHeader() });
      setRecords(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (r) => {
    setDownloading(r.id);
    try {
      const res = await axios.get(`${API}/admin/records/${r.id}/download`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setDownloading(null);
    }
  };

  // Group uploads by client
  const byClient = records.reduce((acc, r) => {
    const key = r.client_email || "unknown";
    if (!acc[key]) acc[key] = { name: r.client_name || key, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});
  const clientKeys = Object.keys(byClient);

  return (
    <div data-testid="client-records-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Client Records</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Documents your clients uploaded from their portal — lab results, protocols,
          and other records, grouped by client.
        </p>
      </div>

      {loading ? (
        <div className="text-center text-[#899480] py-10">
          <Loader2 size={18} className="animate-spin inline" /> Loading…
        </div>
      ) : clientKeys.length === 0 ? (
        <div
          className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center"
          data-testid="admin-records-empty"
        >
          <FileText size={26} strokeWidth={1.3} className="mx-auto text-[#899480] mb-3" />
          <div className="font-serif text-xl text-[#3F5043]">No uploads yet.</div>
          <p className="text-sm text-[#3F5043]/60 mt-1">
            When clients upload documents in their portal, they appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {clientKeys.map((email) => (
            <div key={email} data-testid={`admin-records-client-${email}`}>
              <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-2">
                {byClient[email].name} · {email}
              </div>
              <div className="space-y-2">
                {byClient[email].items.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`admin-record-${r.id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-5 py-3.5"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={16} className="text-[#899480] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-[#3F5043] truncate">
                          {r.label || r.original_filename}
                        </div>
                        <div className="text-[11px] text-[#899480]">
                          {r.original_filename} · {fmtBytes(r.size)} · {fmtDate(r.created_at)}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`admin-record-download-${r.id}`}
                      onClick={() => download(r)}
                      disabled={downloading === r.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#3F5043] text-xs hover:bg-[#BCB3A6]/40 flex-shrink-0 disabled:opacity-60"
                    >
                      {downloading === r.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
